import DocumentLink from '../documents/DocumentLink'
import { readerPage } from '../../lib/documentReader'
/* The LEDGER BOX (Ale ruling 2026-09-05, flat v4): a second card beneath
 * the Record table = the trades view of the selected position.
 *
 *   title    "Trades of this position — holder · instrument · issuer",
 *            "n movements of the position · m of the instrument", ×
 *   table 1  Movements of the position, oldest first
 *   table 2  Movements of the instrument (issuances / redemptions)
 *   a line   DATE (two-digit day) · MOVEMENT (the kind only) · UNITS
 *            (signed) · DOCUMENT (chip + kind, or "✗ owed · <expected
 *            kind>") · EV · STATE
 *   foot     listed movement history · recorded balance / supply · dated
 *            checkpoint comparisons. A first reference never implies an
 *            acquisition or issuance from zero.
 *   commitment-based positions: COMMITMENT · DRAWN · UNDRAWN columns instead
 *            of UNITS, each movement writing its effect, the foot per column
 *
 * Checkpoints explain the comparison without becoming movement rows.
 * No proposed plug is counted as an actual trade. Server-proved interval
 * arithmetic is presented by positionLedger.ts. */

import { Link } from 'react-router-dom'
import type { RecordContents, RtSegment, RtTrade } from '../RecordTable'
import type { DrawerTarget } from '../ObjectDrawer'
import { humanizeToken } from '../../lib/officerFormat'
import {
  isProven,
  tableDate,
  tradeDocument,
  tradeWords,
  EXPECTED_KIND,
  type EvMatrixRow,
} from './evidenceRows'
import {
  balanceDemand,
  breakTaskOf,
  commitmentFoot,
  effectWords,
  hasBreak,
  instrumentMovements,
  isCommitmentBased,
  positionMovements,
  statedCommitment,
  unitsFoot,
  unitsWords,
  type CommitmentEffect,
  type LedgerTrade,
  type Movement,
  type DatedMovementLedger,
} from './positionLedger'
import { figure, deltaWords, legPositionIds, type DeriveTask } from './recordDerive'

interface Props {
  fundId?: string
  seg: RtSegment
  table: RecordContents
  fullTable?: RecordContents
  matrix: EvMatrixRow[]
  tasks: DeriveTask[]
  breakHref?: string
  addMovementHref?: string
  onClose: () => void
  onOpen?: (target: DrawerTarget) => void
  onEvidence?: (target: DrawerTarget) => void
}

const SEP = <span className="sep">·</span>

function DocChip({
  fundId,
  id,
  page,
}: {
  fundId?: string
  id: string | null
  name: string
  page: string | null
}) {
  if (!id)
    return (
      <span className="fa-docchip still" data-testid="ledger-doc">
        Source reference unavailable
      </span>
    )
  const cited = readerPage(page?.replace(/^p\./, '').split('–')[0])
  return (
    <DocumentLink
      className="fa-docchip"
      document={{ document_id: id, page: cited }}
      context={{ fundId, tab: 'record' }}
      onClick={(e) => e.stopPropagation()}
      data-testid="ledger-doc"
      title="Open the document"
    />
  )
}

export default function PositionLedger({
  fundId,
  seg,
  table,
  fullTable = table,
  matrix,
  tasks,
  breakHref,
  addMovementHref,
  onClose,
  onOpen,
  onEvidence,
}: Props) {
  const trades = table.trades as LedgerTrade[]
  const cb = isCommitmentBased(seg)
  // The combined record may carry an automatic or officer-derived figure.
  // Its amount is on record, but it is not a statement's balance (batch F).
  const legIds = new Set(legPositionIds(seg))
  const derivedBalance =
    seg.balance_source === 'balance+trades' ||
    seg.balance_source === 'trades' ||
    fullTable.positions.some((p) => legIds.has(p.id) && p.balance_provenance === 'movement_derived')
  const officerBalance = fullTable.positions.some(
    (position) => legIds.has(position.id) && position.balance_provenance === 'officer_derived'
  )
  const posMoves = positionMovements(seg, trades)
  const insMoves = instrumentMovements(seg, trades)
  const instrument = seg.instrument_id
    ? (fullTable.instruments.find((i) => i.id === seg.instrument_id) ?? null)
    : null
  const byObject = new Map<string, EvMatrixRow[]>()
  for (const r of matrix) byObject.set(r.object_id, [...(byObject.get(r.object_id) ?? []), r])
  const task = breakTaskOf(seg, tasks)
  const reconcileTo =
    breakHref ??
    (fundId
      ? task
        ? `/v2/workbench/reconciliation?fund=${encodeURIComponent(fundId)}&task=${task.task_id}`
        : `/v2/workbench/reconciliation?fund=${encodeURIComponent(fundId)}`
      : null)

  /* the position's statement: its balance demand (B1 / B2) names the document */
  const balanceRow = balanceDemand(seg, matrix)
  const stated = cb ? statedCommitment(seg) : null
  const legs = fullTable.positions.filter((position) => legIds.has(position.id))
  const positionLedger = legs.find((position) => position.id === seg.position_id)?.movement_ledger
  const commitmentLedger = legs.find(
    (position) => position.position_role === 'commitment'
  )?.movement_ledger
  const undrawnLedger = legs.find(
    (position) => position.position_role === 'undrawn'
  )?.movement_ledger
  const posFoot = cb
    ? commitmentFoot(posMoves, stated as CommitmentEffect, seg.as_of, {
        commitment: commitmentLedger,
        undrawn: undrawnLedger,
      })
    : unitsFoot(posMoves, seg.units_held, seg.as_of, positionLedger)

  /* the instrument's statement: the issued total, its B3 row's document */
  const b3 = seg.instrument_id
    ? ((byObject.get(seg.instrument_id) ?? []).find(
        (r) => r.obligation_code === 'B3' && (r.document_id || r.document_name)
      ) ??
      (byObject.get(seg.instrument_id) ?? []).find((r) => r.obligation_code === 'B3') ??
      null)
    : null
  const supply = instrument?.outstanding_units ?? null
  const supplyDate = instrument?.balance_as_of ?? b3?.dated ?? instrument?.issuance_date ?? null
  const insFoot = cb
    ? commitmentFoot(insMoves, { commitment: supply, drawn: null, undrawn: null }, supplyDate, {
        commitment: instrument?.movement_ledger,
      })
    : unitsFoot(insMoves, supply, supplyDate, instrument?.movement_ledger)

  const n = posMoves.length
  const m = insMoves.length

  const evOf = (tradeId: string) => {
    const rows = byObject.get(tradeId) ?? []
    if (rows.length === 0) return null
    const proven = rows.filter((r) => isProven(r.status)).length
    return { proven, total: rows.length }
  }

  const evidenceDoor = onEvidence ?? onOpen
  const recorded = (trade: RtTrade) => trade.state === 'recorded' || trade.state === 'evidenced'

  const docCell = (t: RtTrade) => {
    const m1 = (byObject.get(t.id) ?? []).find((r) => r.obligation_code === 'M1') ?? null
    if (m1 && (m1.document_id || m1.document_name))
      return (
        <>
          <DocChip
            fundId={fundId}
            id={m1.document_id ?? null}
            name={m1.document_name ?? 'document'}
            page={m1.page ? `p.${m1.page}` : null}
          />
          {m1.document_kind ? (
            <>
              {' '}
              <span className="kind">{humanizeToken(m1.document_kind)}</span>
            </>
          ) : null}
        </>
      )
    if (t.document_id)
      return (
        <DocumentLink
          className="fa-docchip"
          document={t.document_ref ?? { document_id: t.document_id }}
          context={{ fundId, tab: 'record', focus: `trade:${t.id}` }}
          data-testid="ledger-doc"
        />
      )
    const own = tradeDocument(t.document)
    if (own) return <span className="fa-docchip still">Source reference unavailable</span>
    if (m1 && !isProven(m1.status) && m1.is_mandatory)
      return evidenceDoor && recorded(t) ? (
        <button
          type="button"
          className="fa-docchip miss"
          data-testid="ledger-owed"
          title="Open the missing evidence for this movement"
          onClick={(event) => {
            event.stopPropagation()
            evidenceDoor({ type: 'trade', id: t.id })
          }}
        >
          ✗ owed{SEP}
          <span className="kind">{EXPECTED_KIND.M1}</span>
        </button>
      ) : (
        <span className="miss" data-testid="ledger-owed">
          ✗ owed{SEP}
          <span className="kind">{EXPECTED_KIND.M1}</span>
        </span>
      )
    return <span className="fa-none">—</span>
  }

  const movementRow = (mv: Movement, kind: 'pos' | 'ins') => {
    const t = mv.trade as RtTrade & { recallable?: boolean | null }
    const ev = evOf(t.id)
    const q =
      t.recallable === false ? 'recallable: no' : t.recallable === true ? 'recallable: yes' : null
    return (
      <tr className={kind} key={t.id} data-testid="ledger-movement" data-trade={t.id}>
        <td className="dt">
          {tableDate(t.effective_date) ?? '—'}
          {t.effective_date_origin === 'document_receipt' && <span className="q">received</span>}
        </td>
        <td className="kd" data-testid="ledger-kind">
          <span className="arr" aria-hidden="true">
            →
          </span>
          {onOpen && recorded(t) ? (
            <button
              type="button"
              className="fa-docchip"
              data-testid="ledger-movement-door"
              title="Open this movement in the Record"
              onClick={(event) => {
                event.stopPropagation()
                onOpen({ type: 'trade', id: t.id })
              }}
            >
              {tradeWords(t.transaction_type)}
            </button>
          ) : (
            tradeWords(t.transaction_type)
          )}
          {t.source_stage && (
            <span className="q">
              {t.source_stage === 'provisional'
                ? 'Provisional draft'
                : t.source_stage === 'final_terms'
                  ? 'Final terms recorded'
                  : 'Source status unavailable'}
            </span>
          )}
          {q ? <span className="q">{q}</span> : null}
        </td>
        {cb && mv.effect ? (
          <>
            <td className="num u" data-testid="ledger-commitment">
              {effectWords(mv.effect.commitment)}
            </td>
            <td className="num u" data-testid="ledger-drawn">
              {effectWords(mv.effect.drawn)}
            </td>
            <td className="num u" data-testid="ledger-undrawn">
              {effectWords(mv.effect.undrawn)}
            </td>
          </>
        ) : (
          <td className="num u" data-testid="ledger-units">
            {unitsWords(mv)}
          </td>
        )}
        <td className="doc" data-testid="ledger-document">
          {docCell(t)}
        </td>
        <td className="num">
          {ev && evidenceDoor && recorded(t) ? (
            <button
              type="button"
              className={`fa-docchip ev${ev.proven < ev.total ? ' part' : ''}`}
              data-testid="ledger-ev"
              title="Open evidence for this movement"
              onClick={(event) => {
                event.stopPropagation()
                evidenceDoor({ type: 'trade', id: t.id })
              }}
            >
              {ev.proven}/{ev.total}
            </button>
          ) : ev ? (
            <span className={`ev${ev.proven < ev.total ? ' part' : ''}`} data-testid="ledger-ev">
              {ev.proven}/{ev.total}
            </span>
          ) : (
            <span className="fa-none">—</span>
          )}
        </td>
        <td>
          <span className="fa-st st-rec" data-testid="ledger-state">
            ON RECORD
          </span>
        </td>
      </tr>
    )
  }

  const figs = (v: number | null) => (v === null ? '' : figure(v))
  const deltaFig = (v: number) => (v < 0 ? deltaWords(v) : figure(v))
  const brkFig = (v: number | null) => (v === null || v === 0 ? '' : deltaFig(v))

  const footRows = (
    foot: ReturnType<typeof unitsFoot> | ReturnType<typeof commitmentFoot>,
    word: 'balance' | 'supply',
    date: string | null,
    doc: { id: string | null; name: string } | null,
    testId: string,
    derived = false,
    ledgers: { label: string; ledger?: DatedMovementLedger | null; sign?: number }[] = [],
    officerRecorded = false
  ) => {
    const isCb = typeof foot.movements !== 'number'
    const cells = (v: number | CommitmentEffect | null, brk = false) => {
      if (isCb) {
        const e = (v ?? { commitment: null, drawn: null, undrawn: null }) as CommitmentEffect
        const f = brk ? brkFig : figs
        return (
          <>
            <td className="num">{f(e.commitment)}</td>
            <td className="num">{f(e.drawn)}</td>
            <td className="num">{f(e.undrawn)}</td>
          </>
        )
      }
      const x = v as number | null
      return <td className="num">{brk ? brkFig(x) : figs(x)}</td>
    }
    const statedKnown = isCb
      ? [
          (foot.stated as CommitmentEffect).commitment,
          (foot.stated as CommitmentEffect).drawn,
          (foot.stated as CommitmentEffect).undrawn,
        ].some((v) => v !== null)
      : foot.stated !== null
    const broken = hasBreak(foot.break as number | null | CommitmentEffect)
    return (
      <>
        <tr className="lf first" data-testid={`${testId}-movements`}>
          <td className="lab" colSpan={2}>
            listed movements
          </td>
          {cells(foot.movements as number | CommitmentEffect)}
          <td className="dr" colSpan={3} />
        </tr>
        <tr className="lf" data-testid={`${testId}-stated`}>
          <td className="lab" colSpan={2}>
            {derived ? 'derived' : officerRecorded ? 'recorded' : 'stated'} {word}
            {date ? (
              <>
                {SEP}
                <span className="dt">{tableDate(date)}</span>
              </>
            ) : null}
            {doc ? (
              <>
                {SEP}
                <DocChip fundId={fundId} id={doc.id} name={doc.name} page={null} />
              </>
            ) : null}
          </td>
          {cells(foot.stated as number | CommitmentEffect)}
          <td className="dr" colSpan={3} />
        </tr>
        {ledgers.flatMap(({ label, ledger, sign = 1 }) =>
          (ledger?.intervals ?? []).map((interval) => (
            <tr
              className="lf"
              key={`${label}:${interval.latest?.id}`}
              data-testid={`${testId}-comparison`}
            >
              <td colSpan={isCb ? 8 : 6}>
                {label}
                {' · '}
                {interval.previous && interval.latest ? (
                  <>
                    {figure(sign * interval.previous.value)} at {tableDate(interval.previous.date)}
                    {interval.expected !== null ? (
                      <>
                        {' '}
                        + {figure(sign * (interval.expected - interval.previous.value))} movements ={' '}
                        {figure(sign * interval.expected)}
                      </>
                    ) : (
                      ' + movements not fully established'
                    )}
                    {'; recorded '}
                    {figure(sign * interval.latest.value)} at {tableDate(interval.latest.date)}
                    {interval.residual !== null && interval.residual !== 0 ? (
                      <>; unexplained {figure(sign * interval.residual)}</>
                    ) : null}
                  </>
                ) : interval.latest ? (
                  <>
                    Opening reference {figure(sign * interval.latest.value)} at{' '}
                    {tableDate(interval.latest.date)}
                  </>
                ) : (
                  'Dated reference unavailable'
                )}
                {interval.status === 'unknown' ? ' · comparison unavailable' : null}
              </td>
            </tr>
          ))
        )}
        <tr
          className={`lf ${broken ? 'brk' : 'nb'}`}
          data-testid={`${testId}-break`}
          data-break={broken ? 'open' : 'none'}
        >
          <td className="lab" colSpan={2}>
            {!statedKnown
              ? `no stated ${word}`
              : broken
                ? 'break'
                : (
                      isCb
                        ? Object.values(foot.break as CommitmentEffect).every(
                            (value) => value === null
                          )
                        : foot.break === null
                    )
                  ? 'dated comparison unavailable'
                  : 'no break'}
            {broken && foot.later > 0 && date ? (
              <span className="q">to {tableDate(date)}</span>
            ) : null}
          </td>
          {broken ? (
            cells(foot.break as number | CommitmentEffect, true)
          ) : isCb ? (
            <>
              <td />
              <td />
              <td />
            </>
          ) : (
            <td />
          )}
          <td className="dr" colSpan={3}>
            {broken && reconcileTo ? (
              <Link className="fa-brkchip" to={reconcileTo} data-testid={`${testId}-reconcile`}>
                {breakHref ? 'Open break ›' : 'reconcile ›'}
              </Link>
            ) : null}
          </td>
        </tr>
      </>
    )
  }

  const cols = cb ? (
    <colgroup>
      <col style={{ width: '9.8%' }} />
      <col style={{ width: '21.6%' }} />
      <col style={{ width: '10%' }} />
      <col style={{ width: '10%' }} />
      <col style={{ width: '10%' }} />
      <col style={{ width: '20.6%' }} />
      <col style={{ width: '5.8%' }} />
      <col style={{ width: '12.2%' }} />
    </colgroup>
  ) : (
    <colgroup>
      <col style={{ width: '9.8%' }} />
      <col style={{ width: '24.9%' }} />
      <col style={{ width: '11.6%' }} />
      <col style={{ width: '35.7%' }} />
      <col style={{ width: '5.8%' }} />
      <col style={{ width: '12.2%' }} />
    </colgroup>
  )
  const head = (
    <thead>
      <tr>
        <th>date</th>
        <th>movement</th>
        {cb ? (
          <>
            <th className="num">commitment</th>
            <th className="num">drawn</th>
            <th className="num">undrawn</th>
          </>
        ) : (
          <th className="num">units</th>
        )}
        <th>document</th>
        <th className="num">ev</th>
        <th>state</th>
      </tr>
    </thead>
  )

  return (
    <div
      className="fa-card fa-ledger"
      data-testid="record-ledger"
      data-position={seg.position_id}
      data-commitment={cb || undefined}
      role="region"
      aria-label="Trades of this position"
    >
      <div className="fa-lhd">
        <span className="ttl">Trades of this position</span>
        <span className="dash">—</span>
        <span className="who" data-testid="record-ledger-title">
          {seg.holder_name ?? 'a holder'}
          {SEP}
          <span className="in">
            <span className="tdot pos" aria-hidden="true" />
            {seg.instrument_name ?? 'instrument'}
          </span>
          {SEP}
          {seg.issuer_name ?? 'an issuer'}
        </span>
        <span className="cnt" data-testid="record-ledger-count">
          <b>{n}</b> movement{n === 1 ? '' : 's'} of the position · <b>{m}</b> of the instrument
        </span>
        {addMovementHref ? (
          <Link className="vbtn" to={addMovementHref} data-testid="record-add-movement">
            Add movement
          </Link>
        ) : null}
        <button
          type="button"
          className="x"
          onClick={onClose}
          aria-label="Close the trades box"
          title="close (Esc)"
          data-testid="record-ledger-close"
        >
          ×
        </button>
      </div>
      <div className="fa-lcap">
        <span className="tdot pos" aria-hidden="true" />
        Movements of the position <span className="ord">· oldest first</span>
      </div>
      <table className="fa-mv" data-testid="ledger-position">
        {cols}
        {head}
        <tbody>
          {posMoves.map((mv) => movementRow(mv, 'pos'))}
          {footRows(
            posFoot,
            'balance',
            seg.as_of,
            !derivedBalance &&
              !officerBalance &&
              balanceRow &&
              (balanceRow.document_id || balanceRow.document_name)
              ? { id: balanceRow.document_id ?? null, name: balanceRow.document_name ?? 'document' }
              : null,
            'ledger-position',
            derivedBalance,
            cb
              ? [
                  { label: 'Commitment', ledger: commitmentLedger },
                  { label: 'Undrawn', ledger: undrawnLedger, sign: -1 },
                ]
              : [{ label: 'Balance', ledger: positionLedger }],
            officerBalance
          )}
        </tbody>
      </table>
      <div className="fa-lcap">
        <span className="tdot ins" aria-hidden="true" />
        Movements of the instrument <span className="ord">· oldest first</span>
      </div>
      <table className="fa-mv" data-testid="ledger-instrument">
        {cols}
        {head}
        <tbody>
          {insMoves.map((mv) => movementRow(mv, 'ins'))}
          {footRows(
            insFoot,
            'supply',
            supplyDate,
            b3 && (b3.document_id || b3.document_name)
              ? { id: b3.document_id ?? null, name: b3.document_name ?? 'document' }
              : null,
            'ledger-instrument',
            false,
            [{ label: 'Issued amount', ledger: instrument?.movement_ledger }]
          )}
        </tbody>
      </table>
    </div>
  )
}
